import { padStart, pick, sortBy, uniq, uniqBy } from 'lodash-es';
import { AttachmentType } from '~/features/attachment/models/attachment';
import { date } from '~/lib/date';
import { addReadableInfo } from '~/lib/enum/readable';
import { safeSum } from '~/lib/jsTools/number';
import { BaseEntity } from '~/lib/model/base';
import { calculateDailyPriceByFeePeriod, IAction } from '~/models/common';
import { Company } from '~/models/company';
import { EnergyType, IMirnDetailPageResponse, INmiDetailPageResponse, MIRN, NMI } from '~/models/meter';
import { IUser, User } from '~/models/user';
import { MeterGroup } from '~/pages/dashboard/project/EAReport/types';
import { FeePeriod, GreenBillingCalculation, Retailer } from './retailer';

export enum ProjectType {
  NEW = 'NEW',
  RENEW = 'RENEW'
}

export enum ProjectPriorityType {
  EXTREME = 'EXTREME',
  URGENT = 'URGENT',
  NORMAL = 'NORMAL'
}

export const ProjectAttachmentType = pick(AttachmentType, [
  'BILL',
  'BILLS_AND_DATA',
  'CONTRACTS',
  'CUSTOMER_ENGAGEMENT',
  'IA_FORM',
  'OTHERS'
]);

addReadableInfo(ProjectAttachmentType, {
  BILL: 'Bill',
  BILLS_AND_DATA: 'Data',
  CONTRACTS: 'Retailer Contract',
  CUSTOMER_ENGAGEMENT: 'Customer Engagement & LOA',
  IA_FORM: 'IA Form',
  OTHERS: 'Others'
});

export type ProjectAttachmentType =
  | AttachmentType.BILL
  | AttachmentType.IA_FORM
  | AttachmentType.CUSTOMER_ENGAGEMENT
  | AttachmentType.BILLS_AND_DATA
  | AttachmentType.CONTRACTS
  | AttachmentType.OTHERS;

export enum ProjectStatus {
  PROSPECT = 'PROSPECT',
  IA_REQUESTED = 'IA_REQUESTED',
  IA_ASSIGNED = 'IA_ASSIGNED',
  IA_FINISHED = 'IA_FINISHED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  OPPORTUNITY_CANCELLED = 'OPPORTUNITY_CANCELLED',
  ENGAGED = 'ENGAGED',
  PROJECT_PREPARATION = 'PROJECT_PREPARATION',
  PROJECT_APPROVED = 'PROJECT_APPROVED',
  ON_HOLD = 'ON_HOLD',
  ON_HOLD_REQUESTED = 'ON_HOLD_REQUESTED',
  TENDER_PREPARING = 'TENDER_PREPARING',
  TENDER_REQUESTED = 'TENDER_REQUESTED',
  TENDER_ASSIGNED = 'TENDER_ASSIGNED',
  TENDERED = 'TENDERED',
  TENDER_CLOSED = 'TENDER_CLOSED',
  EA_PREPARING = 'EA_PREPARING',
  EA_COMPLETED = 'EA_COMPLETED',
  EA_REPORT_SENT = 'EA_REPORT_SENT',
  REPRICED = 'REPRICED',
  OFFER_REJECT_REQUESTED = 'OFFER_REJECT_REQUESTED',
  OFFER_REJECTED = 'OFFER_REJECTED',
  OFFER_SIGNED = 'OFFER_SIGNED',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  BACK_TO_SALES_AFTER_REJECT_PROJECT_REQUESTED = 'BACK_TO_SALES_AFTER_REJECT_PROJECT_REQUESTED',
  NOT_ACCEPT_OFFER_REQUESTED = 'NOT_ACCEPT_OFFER_REQUESTED'
}

export interface IProjectStatusReportItem {
  status: ProjectStatus;
  count: number;
}

export interface IAssignedUserProjectStatusReportItem extends IProjectStatusReportItem {
  assignedSalesCount: number;
  createdSalesCount: number;
  assignedIAEmcCount: number;
  assignedTenderEmcCount: number;
}

export enum ProcessProjectAction {
  REQUEST_IA = 'REQUEST_IA',
  ASSIGNED_IA = 'ASSIGNED_IA',
  FINISH_IA = 'FINISH_IA',
  DISCONTINUE = 'DISCONTINUE',
  REGISTER_ENGAGEMENT = 'REGISTER_ENGAGEMENT',
  PREPARE_PROJECT = 'PREPARE_PROJECT',
  APPROVE_PROJECT = 'APPROVE_PROJECT',
  REQUEST_MORE_DOCS = 'REQUEST_MORE_DOCS',
  BACK_TO_PROCESS = 'BACK_TO_PROCESS',
  PROCEED_TENDER = 'PROCEED_TENDER',
  TENDERED = 'TENDERED',
  CLOSE_TENDER = 'CLOSE_TENDER',
  REQUEST_TENDER = 'REQUEST_TENDER',
  ASSIGNED_TENDER = 'ASSIGNED_TENDER',
  REQUEST_ON_HOLD = 'REQUEST_ON_HOLD',
  APPROVE_ON_HOLD = 'APPROVE_ON_HOLD',
  PREPARE_EA = 'PREPARE_EA',
  COMPLETE_EA = 'COMPLETE_EA',
  SENT_EA_REPORT = 'SENT_EA_REPORT',
  REQUEST_REJECT = 'REQUEST_REJECT',
  APPROVE_REJECT = 'APPROVE_REJECT',
  BACK_TO_SALES_AFTER_REJECT_PROJECT = 'BACK_TO_SALES_AFTER_REJECT_PROJECT',
  RE_TENDER = 'RE_TENDER',
  REPRICE = 'REPRICE',
  SIGN_OFFER = 'SIGN_OFFER',
  ACCEPT_OFFER = 'ACCEPT_OFFER',
  CLOSE_PROJECT = 'CLOSE_PROJECT',
  NOT_ACCEPT_OFFER = 'NOT_ACCEPT_OFFER',
  CANCEL_PROJECT = 'CANCEL_PROJECT'
}

export enum PaymentType {
  COMMISSION = 'COMMISSION',
  ONCE_OFF = 'ONCE_OFF'
}

export interface IProcessProjectParameter {
  assignedIAEmcId?: string;
  assignedTenderEmcId?: string;
  dueDateTime?: string;
}

export interface IProjectDetailResponse {
  id: string;
  meterIds: string[];
  number: string;
  status: ProjectStatus;
  type?: ProjectType;
  assignedIAEmc?: IUser;
  assignedTenderEmc?: IUser;
  assignedSales?: IUser;
  assignedIAEmcId?: string;
  assignedTenderEmcId?: string;
  createdSalesId?: string;
  assignedSalesId?: string;
  priority?: ProjectPriorityType;
  nmis: INmiDetailPageResponse[];
  mirns: IMirnDetailPageResponse[];
  offers?: IProjectOfferPrimaryResponse[];
  onHoldTillTime?: string;
  createdAt: string;
  createdBy: IUser;
  updatedAt?: string;
  updatedBy?: IUser;
  paymentType?: PaymentType;
  commissionRate?: number;
  estAnnualCommission?: number;
  action: IAction;
}

export interface IProjectOfferPrimaryResponse {
  id?: string;
  retailerId: string;
  meterIds: string[];
  retailer: Retailer;
  meteringFeePeriod?: FeePeriod;
  meteringFeePrice?: number;
  suppDMAOrRPFeePeriod?: FeePeriod;
  suppDMAOrRPFeePrice?: number;
  serviceFeePeriod?: FeePeriod;
  serviceFeePrice?: number;
  greenBilling?: GreenBillingCalculation;
  discount?: number;
  validityEndDateTime?: string;
  // New separated periods
  contractRatePeriods?: IContractRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
  // Legacy periods (kept for backward compatibility)
  periods?: IUpsertProjectOfferPeriodRequest[];
  description?: string;
}

export interface ICreateProjectRequest {
  meterIds: string[];
  type?: ProjectType;
  priority?: ProjectPriorityType;
}

export const formatProjectIdentifier = (projectNumber: string): string => {
  const [number, ...date] = projectNumber.split('-');
  return date.join('') + padStart(number, 4, '0');
};

export class Project extends BaseEntity implements IProjectDetailResponse {
  id!: string;
  meterIds!: string[];
  number!: string;
  status!: ProjectStatus;
  type?: ProjectType;
  assignedIAEmcId?: string;
  assignedTenderEmcId?: string;
  createdSalesId?: string;
  assignedSalesId?: string;
  assignedIAEmc?: IUser;
  assignedTenderEmc?: IUser;
  assignedSales?: IUser;
  priority?: ProjectPriorityType;
  nmis!: INmiDetailPageResponse[];
  mirns!: IMirnDetailPageResponse[];
  offers?: IProjectOfferPrimaryResponse[];
  startDate?: string;
  endDate?: string;
  onHoldTillTime?: string;
  createdAt!: string;
  createdBy!: IUser;
  updatedAt?: string;
  updatedBy?: IUser;
  paymentType?: PaymentType;
  commissionRate?: number;
  estAnnualCommission?: number;
  action!: IAction;

  static override from<T = Project>(raw: any): T {
    return Object.assign(new Project(), {
      ...raw,
      offers: (raw?.offers ?? []).map((offer: any) => ProjectOffer.from(offer))
    });
  }

  get meters(): (INmiDetailPageResponse | IMirnDetailPageResponse)[] {
    return [
      ...(this.nmis || []).map((v) => ({ ...v, energyType: EnergyType.ELECTRICITY } as INmiDetailPageResponse)),
      ...(this.mirns || []).map((v) => ({ ...v, energyType: EnergyType.GAS } as IMirnDetailPageResponse))
    ];
  }

  get companies(): Company[] {
    const { nmis = [], mirns = [] } = this;
    return uniqBy([...nmis, ...mirns].map(({ company }) => company).filter((v) => !!v) as Company[], 'id');
  }

  get companyIds(): string[] {
    const { nmis = [], mirns = [] } = this;
    return uniq([...nmis, ...mirns].map(({ company }) => company?.id).filter((v) => !!v) as string[]);
  }

  get projectIdentifier(): string {
    return formatProjectIdentifier(this.number);
  }

  get isOngoingProject(): boolean {
    return ![
      ProjectStatus.CLOSED,
      ProjectStatus.CANCELLED,
      ProjectStatus.OPPORTUNITY_CANCELLED,
      ProjectStatus.OFFER_REJECTED
    ].includes(this.status);
  }

  getMeterById(meterId: string): INmiDetailPageResponse | IMirnDetailPageResponse | undefined {
    const nmi = this.nmis.find((it) => it.id === meterId);
    if (nmi) {
      return nmi;
    }
    const mirn = this.mirns.find((it) => it.id === meterId);
    if (mirn) {
      return mirn;
    }
  }

  getMetersById(meterIds: string[]): (INmiDetailPageResponse | IMirnDetailPageResponse)[] {
    return meterIds
      .map((it) => this.getMeterById(it))
      .filter((it): it is INmiDetailPageResponse | IMirnDetailPageResponse => !!it);
  }

  isAccountManager(userId: string | undefined) {
    if (!userId) {
      return false;
    }
    return this.companies.some(
      (company) => company.accountManager?.id === userId || company.accountManagerId === userId
    );
  }

  // kWh .p.a
  static calculateAnnualUsage(meters: (NMI | MIRN)[]): number {
    const calculateMeterUsage = (meter: NMI | MIRN) => {
      if (meter instanceof MIRN) {
        return meter.annualContractQuantity ?? 0;
      }

      const latestContract = meter.contracts?.[0];
      return latestContract
        ? safeSum(
            latestContract.contractOffPeakEnergy,
            latestContract.contractPeakEnergy,
            latestContract.contractShoulderEnergy
          )
        : safeSum(meter.annualEstOffPeakEnergy, meter.annualEstPeakEnergy, meter.annualEstShoulderEnergy);
    };

    return meters.reduce((usage, meter) => usage + calculateMeterUsage(meter), 0);
  }
}

export interface IProjectQueryResponse {
  id: string;
  meterIds: string[];
  number: string;
  type?: ProjectType;
  priority?: ProjectPriorityType;
  status: string;
  createdAt: string;
  updatedAt: string;
  statusDueDateTime?: string;
  onHoldTillTime?: string;
  createdBy: IUser;
  assignedIAEmc?: IUser;
  assignedTenderEmc?: IUser;
  assignedSales?: IUser;
  nmis: INmiDetailPageResponse[];
  mirns: IMirnDetailPageResponse[];
  action: IAction;
  estAnnualCommission?: number;
}

export type ITemporaryProject = Pick<Project, 'type' | 'priority' | 'meterIds' | 'companyIds'>;

export interface IUpdateProjectRequest {
  meterIds?: string[];
  priority?: ProjectPriorityType;
  type?: ProjectType;
}

export interface IProcessProjectRequest {
  action: ProcessProjectAction;
  processProjectParameter?: IProcessProjectParameter;
}

export enum ProjectSearchCriteriaSortBy {
  CREATED_DATE = 'CREATED_DATE',
  UPDATED_DATE = 'UPDATED_DATE',
  PROJECT_NUMBER = 'METER_NUMBER',
  PROJECT_TYPE = 'PROJECT_TYPE',
  PROJECT_STATUS = 'PROJECT_STATUS',
  PROJECT_PRIORITY = 'PROJECT_PRIORITY',
  ANNUAL_COMMISSION = 'ANNUAL_COMMISSION',
  ASSIGNED_IA_EMC = 'ASSIGNED_IA_EMC',
  ASSIGNED_TENDER_EMC = 'ASSIGNED_TENDER_EMC',
  ASSIGNED_SALES = 'ASSIGNED_SALES'
}

export enum ProjectSearchCriteriaOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface ProjectSearchCriteria {
  number?: string;
  companyName?: string;
  companyAbn?: string;
  companyAcn?: string;
  meterNumber?: string;
  type?: ProjectType;
  priority?: ProjectPriorityType;
  statuses?: ProjectStatus[];
  statusDueDateFrom?: string | null;
  statusDueDateTo?: string | null;
  statusStartDateFrom?: string | null;
  statusStartDateTo?: string | null;
  assignedIAEmcId?: string;
  assignedTenderEmcId?: string;
  assignedSalesId?: string;
  createdBy?: string;
  sortBy?: ProjectSearchCriteriaSortBy;
  order?: ProjectSearchCriteriaOrder;
}

export enum EnergyPricingOfferTypes {
  FLAT = 'FLAT',
  STEPPED = 'STEPPED'
}

export enum GreenPricingTypes {
  CAL_CERT = 'CAL_CERT',
  CAL_RATE = 'CAL_RATE',
  MAT_CERT = 'MAT_CERT',
  MAT_RATE = 'MAT_RATE',
  PDRS = 'PDRS',
  REPS = 'REPS'
}

export type GreenPricingType = 'lret' | 'sres' | 'ess' | 'veet' | 'aeeis' | 'pdrs' | 'reps';

// ============================================================================
// New Separated Period Interfaces
// ============================================================================

// Base Period Interface
interface IProjectOfferPeriodBase {
  id?: string;
  periodName: number;
  startDate: string;
  endDate: string;
  description?: string;
}

// Contract Rate Period - Contains only contract rate related data
export interface IContractRatePeriod extends IProjectOfferPeriodBase {
  offerType?: EnergyPricingOfferTypes;
  peak?: number;
  shoulder?: number;
  offPeak?: number;
}

// Environmental Rate Period - Contains only environmental rate related data
export interface IEnvironmentalRatePeriod extends IProjectOfferPeriodBase {
  lretType?: GreenPricingTypes;
  lret?: number;
  sresType?: GreenPricingTypes;
  sres?: number;
  essType?: GreenPricingTypes;
  ess?: number;
  veetType?: GreenPricingTypes;
  veet?: number;
  aeeisType?: GreenPricingTypes;
  aeeis?: number;
  pdrsType?: GreenPricingTypes;
  pdrs?: number;
  repsType?: GreenPricingTypes;
  reps?: number;
}

// ============================================================================
// Legacy Period Interface (kept for backward compatibility)
// ============================================================================

export interface IUpsertProjectOfferPeriodRequest {
  id?: string;
  periodName: number;
  offerType?: EnergyPricingOfferTypes;
  startDate: string;
  endDate: string;
  peak?: number;
  shoulder?: number;
  offPeak?: number;
  lretType?: GreenPricingTypes;
  lret?: number;
  sresType?: GreenPricingTypes;
  sres?: number;
  essType?: GreenPricingTypes;
  ess?: number;
  veetType?: GreenPricingTypes;
  veet?: number;
  aeeisType?: GreenPricingTypes;
  aeeis?: number;
  pdrsType?: GreenPricingTypes;
  pdrs?: number;
  repsType?: GreenPricingTypes;
  reps?: number;
  description?: string;
}

export interface IUpsertProjectOfferRequest {
  id?: string;
  retailerId: string;
  meteringFeePeriod?: FeePeriod;
  meteringFeePrice?: number;
  suppDMAOrRPFeePeriod?: FeePeriod;
  suppDMAOrRPFeePrice?: number;
  serviceFeePeriod?: FeePeriod;
  serviceFeePrice?: number;
  greenBilling?: GreenBillingCalculation;
  discount?: number;
  validityEndDateTime?: string;
  // New separated periods
  contractRatePeriods?: IContractRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
  // Legacy periods (kept for backward compatibility)
  periods?: IUpsertProjectOfferPeriodRequest[];
  description?: string;
}

export interface ICreateProjectOfferRequest {
  retailerId: string;
  meterIds?: string[];
  meteringFeePeriod?: FeePeriod;
  meteringFeePrice?: number;
  suppDMAOrRPFeePeriod?: FeePeriod;
  suppDMAOrRPFeePrice?: number;
  serviceFeePeriod?: FeePeriod;
  serviceFeePrice?: number;
  greenBilling?: GreenBillingCalculation;
  discount?: number;
  validityEndDateTime?: string;
  // New separated periods
  contractRatePeriods?: IContractRatePeriod[];
  environmentalRatePeriods?: IEnvironmentalRatePeriod[];
  // Legacy periods (kept for backward compatibility)
  periods?: ICreateProjectOfferPeriodRequest[];
  description?: string;
}

export interface ICreateProjectOfferPeriodRequest {
  periodName: number;
  offerType?: EnergyPricingOfferTypes;
  startDate: string;
  endDate: string;
  peak?: number;
  shoulder?: number;
  offPeak?: number;
  lretType?: GreenPricingTypes;
  lret?: number;
  sresType?: GreenPricingTypes;
  sres?: number;
  essType?: GreenPricingTypes;
  ess?: number;
  veetType?: GreenPricingTypes;
  veet?: number;
  aeeisType?: GreenPricingTypes;
  aeeis?: number;
  pdrsType?: GreenPricingTypes;
  pdrs?: number;
  repsType?: GreenPricingTypes;
  reps?: number;
  serviceStartDate?: string;
  serviceEndDate?: string;
  description?: string;
}

export interface IUpdateProjectOfferPeriodRequest extends ICreateProjectOfferPeriodRequest {
  id: string;
}

export interface IUpdateProjectOfferRequest extends ICreateProjectOfferRequest {
  id?: string;
}

export interface IDeleteProjectOfferRequest {
  id: string;
  contractRatePeriods?: { id: string }[];
  environmentalRatePeriods?: { id: string }[];
  periods?: { id: string }[]; // Legacy, kept for backward compatibility
}

// ============================================================================
// New Separated Period Classes
// ============================================================================

export class ContractRatePeriod extends BaseEntity implements IContractRatePeriod {
  id?: string;
  periodName!: number;
  offerType?: EnergyPricingOfferTypes;
  startDate!: string;
  endDate!: string;
  peak?: number;
  shoulder?: number;
  offPeak?: number;
  description?: string;

  static override from<T = ContractRatePeriod>(raw: any): T {
    return Object.assign(new ContractRatePeriod(), raw);
  }
}

export class EnvironmentalRatePeriod extends BaseEntity implements IEnvironmentalRatePeriod {
  id?: string;
  periodName!: number;
  startDate!: string;
  endDate!: string;
  lretType?: GreenPricingTypes;
  lret?: number;
  sresType?: GreenPricingTypes;
  sres?: number;
  essType?: GreenPricingTypes;
  ess?: number;
  veetType?: GreenPricingTypes;
  veet?: number;
  aeeisType?: GreenPricingTypes;
  aeeis?: number;
  pdrsType?: GreenPricingTypes;
  pdrs?: number;
  repsType?: GreenPricingTypes;
  reps?: number;
  description?: string;

  static override from<T = EnvironmentalRatePeriod>(raw: any): T {
    return Object.assign(new EnvironmentalRatePeriod(), raw);
  }
}

// ============================================================================
// Updated ProjectOffer Class with Backward Compatibility
// ============================================================================

export class ProjectOffer extends BaseEntity implements IProjectOfferPrimaryResponse {
  id?: string;
  meterIds!: string[];
  retailerId!: string;
  retailer!: Retailer;
  meteringFeePeriod?: FeePeriod;
  meteringFeePrice?: number;
  suppDMAOrRPFeePeriod?: FeePeriod;
  suppDMAOrRPFeePrice?: number;
  serviceFeePeriod?: FeePeriod;
  serviceFeePrice?: number;
  greenBilling?: GreenBillingCalculation;
  discount?: number;
  validityEndDateTime?: string;
  // New separated periods
  contractRatePeriods!: ContractRatePeriod[];
  environmentalRatePeriods!: EnvironmentalRatePeriod[];
  // Legacy periods (kept for backward compatibility)
  periods!: ProjectOfferPeriod[];
  description?: string;

  static override from<T = ProjectOffer>(raw: any): T {
    // Handle backward compatibility: convert old format (periods) to new format
    if (raw.periods && raw.periods.length > 0 && (!raw.contractRatePeriods || raw.contractRatePeriods.length === 0)) {
      // Convert old periods to new contractRatePeriods
      raw.contractRatePeriods = raw.periods.map((p: any) => ({
        id: p.id,
        periodName: p.periodName,
        startDate: p.startDate,
        endDate: p.endDate,
        offerType: p.offerType,
        peak: p.peak,
        shoulder: p.shoulder,
        offPeak: p.offPeak,
        description: p.description
      }));

      // Convert old periods to new environmentalRatePeriods
      raw.environmentalRatePeriods = raw.periods.map((p: any) => ({
        id: p.id,
        periodName: p.periodName,
        startDate: p.startDate,
        endDate: p.endDate,
        lretType: p.lretType,
        lret: p.lret,
        sresType: p.sresType,
        sres: p.sres,
        essType: p.essType,
        ess: p.ess,
        veetType: p.veetType,
        veet: p.veet,
        aeeisType: p.aeeisType,
        aeeis: p.aeeis,
        pdrsType: p.pdrsType,
        pdrs: p.pdrs,
        repsType: p.repsType,
        reps: p.reps,
        description: p.description
      }));
    }

    return Object.assign(new ProjectOffer(), {
      ...raw,
      contractRatePeriods: (raw?.contractRatePeriods ?? []).map((period: any) => ContractRatePeriod.from(period)),
      environmentalRatePeriods: (raw?.environmentalRatePeriods ?? []).map((period: any) =>
        EnvironmentalRatePeriod.from(period)
      ),
      periods: sortBy(raw.periods ?? [], (v) => date(v.endDate).valueOf()).map((p: any) => ProjectOfferPeriod.from(p))
    });
  }

  get dailyMeteringFee(): number {
    return calculateDailyPriceByFeePeriod(this.meteringFeePrice, this.meteringFeePeriod);
  }

  get dailySuppDMAOrRPFee(): number {
    return calculateDailyPriceByFeePeriod(this.suppDMAOrRPFeePrice, this.suppDMAOrRPFeePeriod);
  }

  get dailyServiceFee(): number {
    return calculateDailyPriceByFeePeriod(this.serviceFeePrice, this.serviceFeePeriod);
  }
}

export class ProjectOfferPeriod extends BaseEntity implements IUpsertProjectOfferPeriodRequest {
  id!: string;
  periodName!: number;
  offerType?: EnergyPricingOfferTypes;
  startDate!: string;
  endDate!: string;
  peak!: number;
  shoulder!: number;
  offPeak!: number;
  lretType?: GreenPricingTypes;
  lret?: number;
  sresType?: GreenPricingTypes;
  sres?: number;
  essType?: GreenPricingTypes;
  ess?: number;
  veetType?: GreenPricingTypes;
  veet?: number;
  aeeisType?: GreenPricingTypes;
  aeeis?: number;
  pdrsType?: GreenPricingTypes;
  pdrs?: number;
  repsType?: GreenPricingTypes;
  reps?: number;
  description?: string;

  static override from<T = ProjectOfferPeriod>(raw: any): T {
    return Object.assign(new ProjectOfferPeriod(), {
      ...raw,
      peak: raw.peak || 0.0,
      shoulder: raw.shoulder || 0.0,
      offPeak: raw.offPeak || 0.0
    });
  }

  get numOfDays(): number {
    return date(this.endDate).diff(date(this.startDate), 'day') + 1;
  }

  get startYear(): number {
    return date(this.startDate).year();
  }

  get endYear(): number {
    return date(this.endDate).year();
  }

  get daysStart(): number {
    return (
      date(this.startYear === this.endYear ? this.endDate : `${date(this.endDate).year()}-12-31`).diff(
        date(this.startDate),
        'day'
      ) + 1
    );
  }

  get daysEnd(): number {
    return this.startYear === this.endYear
      ? 0
      : date(this.endDate).diff(`${date(this.endYear).year()}-01-01`, 'day') + 1;
  }
}

interface IOperatorResponse {
  id: string;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  title: string;
}

export interface IProjectHistoryQueryResponse {
  id: string;
  projectId: string;
  status: ProjectStatus;
  createdAt: string;
  operator: IOperatorResponse;
  description?: string;
}

export interface IUpdateProjectReportSettingsRequest {
  reportType: string;
  contactWithId: string;
  companyId: string;
  customizedCompanyName: string;
  meterGroups: MeterGroup[];
}

export interface IProjectReportSettingsQueryResponse {
  id: string;
  projectId: string;
  reportType: string;
  contactWith: User;
  company: Company;
  customizedCompanyName?: string;
  meterGroups: MeterGroup[];
}

export class ProjectReportSettings extends BaseEntity implements IProjectReportSettingsQueryResponse {
  id!: string;
  projectId!: string;
  reportType!: string;
  contactWith!: User;
  company!: Company;
  customizedCompanyName?: string;
  meterGroups!: MeterGroup[];

  static override from<T = ProjectReportSettings>(raw: any): T {
    return Object.assign(new ProjectReportSettings(), {
      ...raw,
      contactWith: raw.contactWith ? User.from(raw.contactWith) : raw.contactWith,
      company: raw.company ? Company.from(raw.company) : raw.company,
      meterGroups: JSON.parse(raw.meterGroups || '[]')
    });
  }
}
